<script setup lang="ts">
import * as yup from 'yup'
import { reactive, computed, ref } from 'vue'
import MediumModel from '@/components/Interface/Modal/ModelSizes/MediumModel.vue'
import FormInput from '@/components/Interface/Modal/Forms/FormInput.vue'
import FormSelect from '@/components/Elements/Forms/FormSelect.vue'
import FormGenericError from '@/components/Interface/Modal/Forms/FormGenericError.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import FormProcess from '@/models/formProcess'
import { useGameStore } from '@/stores/gameStore'
import { useBattleSimulatorStore } from '@/stores/battleSimulatorStore'
import { useShipDesignStore } from '@/stores/shipDesignStore'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()
const battleSimulatorStore = useBattleSimulatorStore()
const shipDesignStore = useShipDesignStore()
const formProcess = new FormProcess()

const isAttacking = computed(() => gameStore.modalType === 'battleSimulatorAddAttackingDesign')

const schema = yup.object().shape({
  name: yup
    .string()
    .min(1, () => t('common.validation.nameRequired'))
    .required(() => t('common.validation.nameRequired')),
  hitpoints: yup
    .number()
    .min(0, () => t('common.validation.cannotBeNegative'))
    .required(() => t('common.validation.required')),
  attackVsShips: yup
    .number()
    .min(0, () => t('common.validation.cannotBeNegative'))
    .required(() => t('common.validation.required')),
  attackVsPlanet: yup
    .number()
    .min(0, () => t('common.validation.cannotBeNegative'))
    .required(() => t('common.validation.required')),
  shields: yup
    .number()
    .min(0, () => t('common.validation.cannotBeNegative'))
    .required(() => t('common.validation.required')),
  quantity: yup
    .number()
    .min(1, () => t('common.validation.minValue', { min: 1 }))
    .required(() => t('common.validation.required'))
})

const formValues = reactive({
  name: '',
  hitpoints: 0,
  attackVsShips: 0,
  attackVsPlanet: 0,
  shields: 0,
  quantity: 1
})

const formErrors = reactive<{ [key: string]: string }>({
  generic: '',
  name: '',
  hitpoints: '',
  attackVsShips: '',
  attackVsPlanet: '',
  shields: '',
  quantity: ''
})

const selectedDesignId = ref(0)

const onDesignSelect = () => {
  const design = shipDesignStore.getShipDesignById(selectedDesignId.value)
  if (design) {
    formValues.name = design.name
    formValues.hitpoints = design.hitPoints
    formValues.attackVsShips = design.attackShips
    formValues.attackVsPlanet = design.attackPlanets
    formValues.shields = design.shields
  }
}

const submit = () => {
  schema
    .validate(formValues)
    .then(() => {
      formProcess.resetFormErrors(formErrors)

      const nextId = Date.now()

      const design = {
        id: selectedDesignId.value > 0 ? selectedDesignId.value : nextId,
        name: formValues.name,
        hitpoints: Number(formValues.hitpoints),
        attackVsShips: Number(formValues.attackVsShips),
        attackVsPlanet: isAttacking.value ? Number(formValues.attackVsPlanet) : 0,
        shields: Number(formValues.shields),
        quantity: Number(formValues.quantity)
      }

      if (isAttacking.value) {
        battleSimulatorStore.addAttackingShipDesign(design)
      } else {
        battleSimulatorStore.addDefendingShipDesign(design)
      }

      gameStore.modalType = ''
    })
    .catch((error) => formProcess.processFrontendErrorResponse(error, formErrors))
}

const availableDesigns = computed(() => {
  return Array.from(shipDesignStore.playerShipDesigns.values()).filter(d => !d.deleted)
})

const designOptions = computed(() => [
  { value: 0, label: t('command.battleSimulator.addDesign.selectDesign') },
  ...availableDesigns.value.map((d) => ({ value: d.id, label: d.name }))
])

const selectAndPrefill = (val: string | number) => {
  selectedDesignId.value = Number(val)
  onDesignSelect()
}
</script>

<template>
  <MediumModel :title="isAttacking ? t('command.battleSimulator.addDesign.addAttacking') : t('command.battleSimulator.addDesign.addDefending')">
    <div class="p-2">
      <FormSelect
        :modelValue="selectedDesignId"
        @update:modelValue="selectAndPrefill"
        :options="designOptions"
        :label="t('command.battleSimulator.addDesign.prefillFromDesign')"
      />

      <div class="pt-4">
        <div class="grid grid-cols-2 gap-4">
          <div class="col-span-2">
            <FormInput :label="t('command.battleSimulator.addDesign.labels.name')" v-model="formValues.name" :error="formErrors.name" />
          </div>
          <FormInput :label="t('command.battleSimulator.addDesign.labels.hitpoints')" v-model="formValues.hitpoints" type="number" :min="0" :error="formErrors.hitpoints">
            <template #icon><GenericIcon type="hit_points" /></template>
          </FormInput>
          <FormInput :label="t('command.battleSimulator.addDesign.labels.attackVsShips')" v-model="formValues.attackVsShips" type="number" :min="0" :error="formErrors.attackVsShips">
            <template #icon><GenericIcon type="attack_against_ships" /></template>
          </FormInput>
          <FormInput
            v-if="isAttacking"
            :label="t('command.battleSimulator.addDesign.labels.attackVsPlanet')"
            v-model="formValues.attackVsPlanet"
            type="number"
            :min="0"
            :error="formErrors.attackVsPlanet"
          >
            <template #icon><GenericIcon type="attack_against_planets" /></template>
          </FormInput>
          <FormInput :label="t('command.battleSimulator.addDesign.labels.shields')" v-model="formValues.shields" type="number" :min="0" :error="formErrors.shields">
            <template #icon><GenericIcon type="shields" /></template>
          </FormInput>
          <FormInput :label="t('command.battleSimulator.addDesign.labels.quantity')" v-model="formValues.quantity" type="number" :min="1" :error="formErrors.quantity" />
        </div>
        <div class="flex justify-between pt-4">
          <FormGenericError :error="formErrors.generic" />
          <ActionButton @click="submit">{{ t('command.ships.addDesign') }}</ActionButton>
        </div>
      </div>
    </div>
  </MediumModel>
</template>

<style scoped lang="scss"></style>
